import { IconAlertTriangle, IconFlagQuestion, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

interface FloatingActionProps {
	title: string;
	description?: string;
	className?: string;
	children?: React.ReactNode;
}

const FloatingAction = ({ title, description, className, children }: FloatingActionProps) => {
	const [isVisible, setIsVisible] = useState(false);
	const [isMinimized, setIsMinimized] = useState(true);
	const [atBottom, setAtBottom] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			const scrolledToBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 120;
			setAtBottom(scrolledToBottom);
			setIsVisible(window.scrollY > 80 && !scrolledToBottom);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (
		<>
			<div className="flex flex-row items-center w-full gap-2">{children}</div>

			{!atBottom && isMinimized && isVisible && (
				<motion.button
					className="fixed bottom-6 right-6 flex justify-center items-center w-[44px] h-[44px] bg-primary-600 hover:bg-primary-500 text-primary-foreground rounded-full shadow-lg"
					onClick={() => setIsMinimized(false)}
					initial={{ opacity: 0, scale: 0.8 }}
					animate={{ opacity: 1, scale: 1 }}
					exit={{ opacity: 0, scale: 0.8 }}
					transition={{ duration: 0.2, ease: "easeInOut" }}
				>
					<IconFlagQuestion size={24} stroke={2} />
				</motion.button>
			)}

			<AnimatePresence>
				{!atBottom && isVisible && !isMinimized && (
					<motion.div
						className={clsx(
							"z-50 fixed bottom-6 left-1/2 -translate-x-1/2 min-w-[360px]",
							"flex flex-col rounded-md bg-background border border-border/50 shadow-lg",
							className,
						)}
						initial={{ opacity: 0, y: 10, x: "-50%" }}
						animate={{ opacity: 1, y: 0, x: "-50%" }}
						exit={{ opacity: 0, y: 10, x: "-50%" }}
						transition={{ duration: 0.2, ease: "easeInOut" }}
					>
						<div className="relative flex flex-col justify-center items-center  w-full p-4 gap-4">
							<button type="button" onClick={() => setIsMinimized(true)} className="absolute top-2 right-2 text-foreground opacity-50 hover:opacity-100">
								<IconX size={18} stroke={2} />
							</button>
							<div className="inline-flex justify-center items-center w-[44px] h-[44px] rounded-full bg-slate-100 text-slate-600">
								<IconAlertTriangle size={24} stroke={2} />
							</div>
							<div className="flex flex-col justify-center items-center w-full">
								<span className="text-center text-lg font-semibold text-foreground-dark">{title}</span>
								{description && <span className="text-center text-base text-foreground/70">{description}</span>}
							</div>
							<div className="flex flex-row justify-center w-full gap-1.5">{children}</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};

export default FloatingAction;
